/**
 * A minimal shape for a directive node from mdast that may carry a bracket
 * label `[X]`.  The actual mdast directive types are more constrained, but
 * callers come from multiple packages and pass `any`-typed nodes, so we keep
 * the input loose and validate at runtime.
 *
 * Three node shapes exist in the RMINT codebase:
 *   textDirective / leafDirective:   child[0] is { type: 'text', value: string }
 *   containerDirective:              child[0] is a directiveLabel paragraph whose
 *                                    own child[0] is { type: 'text', value: string }
 * In both cases we also accept attributes.id as a last-resort fallback.
 */
type DirectiveLike = {
  children?: Array<{ type?: string; value?: string; children?: Array<{ type?: string; value?: string }> } | null | undefined> | null;
  attributes?: { id?: string } | null;
};

/**
 * Returns the bracket label for a directive node, or undefined when absent.
 *
 * Resolution order:
 *   1. children[0].value — textDirective / leafDirective path
 *   2. children[0].children[0].value — containerDirective (directiveLabel wrapper) path
 *   3. attributes.id — remark-directive attribute fallback
 *   4. undefined
 *
 * Used by editor directive descriptors (widget-descriptors, slot-descriptor)
 * and the slot-grammar parser.  Exported from @rmint/core/directives/bracket-label.
 */
export function bracketLabelOf(node: DirectiveLike | null | undefined): string | undefined {
  if (!node) return undefined;

  const first = node.children?.[0];
  if (first) {
    // textDirective / leafDirective: child[0] carries the bracket label directly
    if (first.type === 'text' && typeof first.value === 'string') {
      return first.value;
    }
    // containerDirective: child[0] is a directiveLabel paragraph wrapping the text node
    const labelChild = first.children?.[0];
    if (labelChild?.type === 'text' && typeof labelChild.value === 'string') {
      return labelChild.value;
    }
  }

  // Attribute id fallback (used by some remark-directive serialisation paths)
  const id = node.attributes?.id;
  if (typeof id === 'string') return id;

  return undefined;
}
